import {
  Block,
  BlockTitle,
  Button,
  Icon,
  List,
  ListInput,
  Page,
  Popup,
} from "framework7-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import DeleteButton from "../../components/delete-button";
import PopupNavbar from "../../components/popup-navbar";
import { requests } from "../../constants/requests";
import { usePopupBackClose } from "../../hooks/use-back-close";
import { useEnterKeys } from "../../hooks/use-enter-keys";
import { vibrate } from "../../hooks/use-haptics";
import { useIsPending } from "../../hooks/use-is-pending";
import { useToast } from "../../hooks/use-toast";
import { deleteTag, updateTag } from "../../redux/reducer/app-slice";
import { confirmDialog } from "../../utils/confirm-dialog";
import { countLabel } from "../../utils/format";
import {
  MAX_TAG_NAME_LENGTH,
  TAG_COLORS,
  isValidTagName,
  normalizeTagName,
} from "../../utils/tags";

const NAME_INPUT = "tag-name-edit";

const TagEditPopup = ({ groupId, tag, opened, onClose }) => {
  const [name, setName] = useState("");
  const [color, setColor] = useState(null);
  const popupRef = useRef(null);
  const dispatch = useDispatch();
  const { showError } = useToast();
  const isSaving = useIsPending(requests.saveTag);
  const isDeleting = useIsPending(requests.deleteTag);
  const isBusy = isSaving || isDeleting;
  const backClose = usePopupBackClose(opened, () => !isBusy);
  useEnterKeys(backClose.id, { [NAME_INPUT]: { hint: "done" } });

  const currentName = tag?.name ?? "";
  const currentColor = tag?.color ?? null;
  useEffect(() => {
    if (!opened) return;
    setName(currentName);
    setColor(currentColor);
  }, [opened, currentName, currentColor]);

  const closePopup = () => popupRef.current?.close();

  const done = () => {
    vibrate();
    closePopup();
  };

  const save = () => {
    if (isBusy || !tag) return;
    if (!isValidTagName(name)) {
      showError(
        `Bitte gib einen Namen mit höchstens ${MAX_TAG_NAME_LENGTH} Zeichen ein.`,
      );
      return;
    }
    const changes = {
      ...(normalizeTagName(name) !== currentName && {
        name: normalizeTagName(name),
      }),
      ...(color && color !== currentColor && { color }),
    };
    if (Object.keys(changes).length === 0) {
      closePopup();
      return;
    }
    dispatch(updateTag(groupId, tag.id, changes, done, showError));
  };

  const remove = () => {
    if (isBusy || !tag) return;
    const usage = tag.usage ?? 0;
    confirmDialog({
      title: `„${currentName}“ löschen?`,
      text:
        usage > 0
          ? `Der Tag wird von ${countLabel(usage, "Ausgabe", "Ausgaben")} entfernt. Die Ausgaben bleiben erhalten.`
          : "Der Tag wird gelöscht.",
      confirmText: "Löschen",
      destructive: true,
      cssClass: "delete-tag-dialog",
      onConfirm: () => dispatch(deleteTag(groupId, tag.id, done, showError)),
    });
  };

  return (
    <Popup
      {...backClose}
      className="tag-edit-popup"
      opened={opened}
      onPopupOpened={(popup) => {
        popupRef.current = popup;
      }}
      onPopupClosed={onClose}
    >
      <Page>
        <PopupNavbar title="Tag bearbeiten" />
        <Block>
          <List strongIos insetIos outlineIos>
            <ListInput
              name={NAME_INPUT}
              outline
              type="text"
              label="Name"
              floatingLabel
              autocomplete="off"
              autocapitalize="sentences"
              maxlength={MAX_TAG_NAME_LENGTH}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </List>
          <BlockTitle>Farbe</BlockTitle>
          <div className="tag-color-options" role="radiogroup">
            {TAG_COLORS.map((option) => (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={option === color}
                aria-label={`Farbe ${option}`}
                className={[
                  "tag-color-option",
                  option === color && "tag-color-option-selected",
                ]
                  .filter(Boolean)
                  .join(" ")}
                style={{ "--tag-color": option }}
                onClick={() => setColor(option)}
              >
                {option === color && (
                  <Icon ios="f7:checkmark" md="material:check" size={18} />
                )}
              </button>
            ))}
          </div>
        </Block>
        <Block inset className="tag-edit-actions">
          <Button fill disabled={isBusy} onClick={save}>
            Speichern
          </Button>
          <DeleteButton
            className="tag-delete-button"
            busy={isDeleting}
            disabled={isSaving}
            onClick={remove}
          >
            Tag löschen
          </DeleteButton>
        </Block>
      </Page>
    </Popup>
  );
};

export default TagEditPopup;
